import { Resource, Purse } from './resources';
import { AtlasCoord, JsonDictionaryWrapper } from './utils';
import { Mineable } from './deposit';
import { WorldPoint, Direction } from './grid';
import { Distributor, Priority } from './graph';
import { Enemy, BuildingFinder } from './enemy';
import { FireControl, Gun, GunType, Shot } from './gun';

// basic data for a building
export enum BuildingType {
    /* Built for you, basic storage */
    Hq,

    /* This digs out raw materials depending on where it is placed */
    Mine,

    /* Defensive tower for enemies */
    Turret,

    /* Convert uranium ore to turret charges */
    Refinery,

    /* Convert stone to paving stones */
    PavingStoneFactory,

    /* neutral, free waypoint */
    Flag
}

export interface EnemyFinder {
    find_enemy_near(wp: WorldPoint, r: number): Enemy | null
}

export type BuildingImage = {
    root: AtlasCoord,
    dimension: number,
    height: number,
}

export type BuildingFormula = {
    input: Resource | null,
    output: (m: Mineable | null) => Resource,
    duration: number,
}

export type BuildingDescription = {
    name: string,
    image: BuildingImage,
    placeholder_title: string | null,

    // if it returns true to this it will request the resource with priority Stock
    stocks: ((r: Resource) => boolean) | null,

    // if this is not null it will try to hold this level of resource with priority Use
    usage_stock: [Resource, number] | null,

    formulae: BuildingFormula[],
    turret_type: GunType | null,
}

// this is a resource on a journey
export class Parcel {
    public destination: WorldPoint
    private purse: Purse
    public resource: Resource

    private toJSON(): any {
        return {
            destination: this.destination,
            purse: this.purse,
            resource: this.resource,
        }
    }

    public static create_json(obj: JsonDictionaryWrapper): Parcel {
        let p = new Parcel(obj.get_number("resource"), WorldPoint.create_json(obj.get_object("destination")))
        p.purse.clear()
        p.purse = Purse.create_json(obj.get_object("purse"))
        return p
    }

    constructor(r: Resource, dp: WorldPoint) {
        this.purse = new Purse(true)
        this.resource = r
        this.destination = dp
        this.purse.add_resource(1, r)
    }

    public arrived(): void {
        this.purse.clear()
    }
}

const placeholder_2: BuildingImage = {
    root: { i: 9, j: 4 },
    dimension: 2,
    height: 2,
}
const placeholder_3: BuildingImage = {
    root: { i: 9, j: 2 },
    dimension: 3,
    height: 3,
}

export function building_description(bt: BuildingType): BuildingDescription {
    switch (bt) {
        case BuildingType.Hq:
            return {
                name: "Headquarters",
                placeholder_title: null,
                image: {
                    root: { i: 0, j: 5 },
                    dimension: 3,
                    height: 3,
                },
                stocks: (r: Resource) => true,
                usage_stock: null,
                formulae: [],
                turret_type: null,
            }

        case BuildingType.Mine:
            return {
                name: "Mine",
                placeholder_title: null,
                image: {
                    root: { i: 0, j: 10 },
                    dimension: 3,
                    height: 3,
                },
                stocks: null,
                usage_stock: null,
                formulae: [
                    {
                        input: null,
                        output: (m: Mineable | null) => {
                            if (m == null) {
                                // should never have been built
                                throw new Error("Can't mine nothing")
                            }

                            switch (m) {
                                case Mineable.Steel:
                                    return Resource.SteelOre;
                                case Mineable.Uranium:
                                    return Resource.Uranium;
                                case Mineable.Rock:
                                    return Resource.Stone;
                            }
                        },
                        duration: 900,
                    },
                ],
                turret_type: null,
            }

        case BuildingType.Turret:
            return {
                name: "Turret",
                placeholder_title: null,
                image: {
                    root: { i: 7, j: 4 },
                    dimension: 2,
                    height: 3,
                },
                stocks: null,
                usage_stock: [Resource.FuelRod, 3],
                formulae: [],
                turret_type: GunType.Turret,
            }

        case BuildingType.PavingStoneFactory:
            return {
                name: "Paving Stone Factory",
                placeholder_title: "Paving Stones",
                image: placeholder_3,
                stocks: null,
                usage_stock: [Resource.Uranium, 3],
                formulae: [
                    {
                        input: Resource.Stone,
                        output: (m: Mineable | null) => Resource.PavingSlab,
                        duration: 600,
                    },
                ],
                turret_type: null,
            }

        case BuildingType.Refinery:
            return {
                name: "Refinery",
                placeholder_title: null,
                image: {
                    root: { i: 4, j: 2 },
                    dimension: 3,
                    height: 3,
                },
                stocks: null,
                usage_stock: [Resource.Uranium, 3],
                formulae: [
                    {
                        input: Resource.SteelOre,
                        output: (m: Mineable | null) => Resource.Steel,
                        duration: 600,
                    },
                    {
                        input: Resource.Uranium,
                        output: (m: Mineable | null) => Resource.FuelRod,
                        duration: 600,
                    },
                ],
                turret_type: null,
            }

        case BuildingType.Flag:
            return {
                name: "Flag",
                placeholder_title: null,
                image: {
                    root: { i: 5, j: 0 },
                    dimension: 1,
                    height: 1,
                },
                stocks: null,
                usage_stock: null,
                formulae: [],
                turret_type: null,
            }
    }
}

export function cost_for_building(bt: BuildingType): Purse {
    var r = new Purse(false)

    switch (bt) {
        /*
          Tier 1

          Minimal for viability
          This includes no defence
         */
        case BuildingType.Mine:
            r.add_resource(1, Resource.Stone)
            break
        case BuildingType.PavingStoneFactory:
            r.add_resource(1, Resource.Stone)
            break
        case BuildingType.Refinery:
            r.add_resource(1, Resource.Stone)
            break

        /*
          Tier 2

          Minimal for defensibility
         */
        case BuildingType.Turret:
            r.add_resource(1, Resource.Stone)
            r.add_resource(1, Resource.Steel)
            break
    }

    return r
}

type BuildingFormulaState = {
    formula: BuildingFormula,
    t: number,
}

/*
  A single instance of a building

  NB this may not yet be attached to the world, and it may never be
     these are used for provisional commands too
 */
export class Building {
    public building_type: BuildingType

    public did_change: (() => void) | null = null

    // This means that the building has been destroyed, and it is still showing
    private _destroyed: boolean = false

    // This means deleted and removed from the map
    public deleted: boolean = false

    // this is the position of the base of the building, ie the lowest point
    public position: WorldPoint

    // these are resources held at this building
    // TODO could these be private? that would force drop/pickup
    public purse: Purse = new Purse(true)

    // These are parcels "sitting on the doorstep", awaiting pickup
    public waiting_parcels: Parcel[] = []

    // when not null, this is what is required for construction
    public construction_purse: Purse | null

    // what mineable resource does this sit on?
    public underlying_resource: Mineable | null = null 

    private gun: Gun | null

    private _id: number
    static max_id:number = 100000 

    public hp: number

    private running_formula: BuildingFormulaState | null = null

    private toJSON(): any {
        return {
            building_type: this.building_type,
            position: this.position,
            destroyed: this._destroyed,
            purse: this.purse,
            waiting_parcels: this.waiting_parcels,
            construction_purse: this.construction_purse,
            underlying_resource: this.underlying_resource,
            gun: this.gun,
            id: this.id,
            hp: this.hp,
            running_formula: this.running_formula,
        }
    }

    public static create_json(obj: JsonDictionaryWrapper): Building {
        const id = obj.get_number("id")
        const bt = obj.get_number("building_type")
        const pos = WorldPoint.create_json(obj.get_object("position"))

        let b = new Building(id, bt, pos)
        b._destroyed = obj.get_boolean("destroyed")
        b.purse = Purse.create_json(obj.get_object("purse"))
        if (obj.exists("construction_purse")) {
            b.construction_purse = Purse.create_json(obj.get_object("construction_purse"))
        } else {
            b.construction_purse = null
        }
        if (obj.exists("underlying_resource")) {
            b.underlying_resource = obj.get_number("underlying_resource")
        } else {
            b.underlying_resource = null
        }
        b.hp = obj.get_number("hp")
        if (obj.exists("gun")) {
            b.gun = Gun.create_json(obj.get_object("gun"))
        }
        b.waiting_parcels = obj.get_object_array("waiting_parcels").all.map(wp => Parcel.create_json(wp))
        if (obj.exists("running_formula")) {
            b.running_formula = Object.assign({}, obj.get_object("running_formula").value);
        } else {
            b.running_formula = null
        }

        return b 
    }
    
    constructor (id: number, bt: BuildingType, pos: WorldPoint) {
        this.building_type = bt
        this.position = pos
        this.construction_purse = null
        this.hp = this.max_hp
        this._id = id

        if (this._id >= Building.max_id) {
            throw new Error("building id too high")
        }

        if (this.description.turret_type != null) {
            this.gun = new Gun(this.description.turret_type)
        } else {
            this.gun = null
        }
    }

    // hp in range [0, 1]
    public get hp_proportion(): number {
        return this.hp / this.max_hp
    }

    public get inert(): boolean {
        return this._destroyed
    }

    public get max_hp(): number {
        switch (this.building_type) {
            case BuildingType.Hq:
                return 100

            default:
                return 50
        }
    }

    public destroy(): void {
        this.running_formula = null
        this.purse.clear()
        this._destroyed = true
    }

    public get formula_state(): BuildingFormulaState | null {
        return this.running_formula
    }

    public get current_image(): BuildingImage {
        if (this._destroyed) {
            switch (this.description.image.dimension) {
                case 2:
                    return {
                        root: { i: 6, j: 13 },
                        dimension: 2,
                        height: 2,
                    }

                case 3:
                    return {
                        root: { i: 6, j: 10 },
                        dimension: 3,
                        height: 3,
                    }

                default:
                    throw new Error("no sprite root found for destroyed building")
            }
        }

        if (this.construction_purse != null) {
            switch (this.description.image.dimension) {
                case 2:
                    return {
                        root: { i: 3, j: 8 },
                        dimension: 2,
                        height: 2,
                    }

                case 3:
                    return {
                        root: { i: 6, j: 7 },
                        dimension: 3,
                        height: 3,
                    }

                default:
                    throw new Error("no sprite root found for building site")
            }
        }

        return this.description.image
    }

    public did_place(): void {
        if (!cost_for_building(this.building_type).empty()) {
            this.construction_purse = new Purse(true)
        }
    }

    public magic_build(): void {
        this.construction_purse = null
    }

    public wants(incoming: Purse, r: Resource): Priority {
        if (this._destroyed) {
            return Priority.None
        }

        if (this.construction_purse != null) {
            let req = cost_for_building(this.building_type).copy(false)
            req.subtract(incoming)
            if (req.get_count(r) > 0) {
                return Priority.Construction
            }
        } else {
            if (this.description.stocks != null && this.description.stocks(r)) {
                return Priority.Stock
            }

            if (this.description.usage_stock != null) {
                const [dr, dq] = this.description.usage_stock
                let desired = new Purse(false)
                desired.add_resource(dq, dr);
                desired.subtract(incoming)
                desired.subtract(this.purse)
                if (desired.get_count(r) > 0) {
                    return Priority.Use
                }
            }
        }

        return Priority.None
    }

    public drop(p: Parcel): void {
        if (!p.destination.equal_to(this.position)) {
            this.waiting_parcels.push(p)
            return
        }
        
        p.arrived()

        if (this.construction_purse != null) {
            this.construction_purse.add_resource(1, p.resource)
            if (this.construction_purse.equal_to(cost_for_building(this.building_type))) {
                // nuke the resources
                // TODO we should keep any leftovers
                this.construction_purse.clear()
                this.construction_purse = null
                return
            }
        }

        this.purse.add_resource(1, p.resource)
    }

    // will this building allow someone to carry this resource away?
    public will_relinquish(r: Resource): boolean {
        if (this.construction_purse != null) {
            // no need, it should only be here if we want it
            return false
        }

        if (this.description.usage_stock != null) {
            const [r, q] = this.description.usage_stock
            if (this.purse.get_count(r) <= q) {
                return false
            }
        }

        return true
    }

    private types_for_donation(): Resource[] {
        let ret = []
        
        for (let r of this.purse.available_types()) {
            if (!this.will_relinquish(r)) {
                continue
            }
            ret.push(r)
        }

        return ret
    }

    public pickup(next: Building, distributor: Distributor, bf: BuildingFinder): Parcel | null {
        // first check if there are any waiting parcels
        for (let p of this.waiting_parcels) {
            let dest = bf.find_building(p.destination)
            if (dest == null) {
                // building deleted
                this.purse.add_resource(1, p.resource)
                p.arrived()
                continue
            }

            if (distributor.should_route_parcel(this, dest, next)) {
                this.waiting_parcels = this.waiting_parcels.filter(item => item !== p)
                return p
            }
        }

        // look to see if there are any parcels we could create
        let journey = distributor.find_journey(this.types_for_donation(), next, this)
        if (journey != null) {
            let [resource, destination] = journey
            this.purse.subtract_resource(1, resource)
            return new Parcel(resource, destination.position)
        }

        return null
    }

    public notify_did_change(): void {
        if (this.did_change != null) {
            this.did_change()
        }
    }

    private start_formula(f: BuildingFormula): void {
        this.running_formula = {
            formula: f,
            t: 0,
        }
        this.notify_did_change()
    }

    public tick(ef: EnemyFinder, fc: FireControl): void {
        if (this._destroyed) {
            return
        }

        if (this.construction_purse != null) {
            return
        }

        if (this.gun != null) {
            this.gun.tick()

            if (this.purse.get_count(Resource.FuelRod) > 0) {
                if (this.gun.can_fire) {
                    let e = ef.find_enemy_near(this.position, this.gun.range)
                    if (e != null) {
                        fc.fire_shot(this.gun.fire(this.position, e.positions[0]))
                        this.purse.subtract_resource(1, Resource.FuelRod)
                    }
                }
            }
        }

        if (this.running_formula != null) {
            this.running_formula.t += 1
            if (this.running_formula.t == this.running_formula.formula.duration) {
                this.purse.add_resource(1, this.running_formula.formula.output(this.underlying_resource))
                this.running_formula = null   
            }
            this.notify_did_change()
        } else if (this.description.formulae.length > 0) {
            for (let f of this.description.formulae) {
                if (f.input == null) {
                    // mining type
                    this.start_formula(f)
                    break
                } else if (this.purse.get_count(f.input) > 0) {
                    // factory type
                    this.purse.subtract_resource(1, f.input)
                    this.start_formula(f)
                    break
                }
            }
        }
    }

    public get id(): number {
        return this._id
    }

    public get description(): BuildingDescription {
        return building_description(this.building_type)
    }

    public occupied_tiles(): WorldPoint[] {
        var pts: WorldPoint[] = []
        const desc = this.description

        var wpi: WorldPoint = this.position.copy()
        for (var i = 0; i < desc.image.dimension; i += 1) {
            var wpj: WorldPoint = wpi.copy()
            for (var j = 0; j < desc.image.dimension; j += 1) {
                pts.push(wpj.copy())
                wpj.translate(Direction.NE)
            }
            wpi.translate(Direction.NW)
        }

        return pts
    }
}


